import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Typography,
  Box,
  Button,
  Chip,
  Paper,
  CircularProgress,
  Grid,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as ResolvedIcon,
  SendOutlined as SendIcon,
  ChatBubbleOutline as ChatIcon,
  VisibilityOffOutlined as EyeSlashIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { helpRequestService } from '../../services/help-request.service';
import { HelpRequest, HelpRequestMessage, HelpRequestStats, MessageReaction as MessageReactionType } from '../../types/help-request.types';
import MessageReactions from '../../components/chat/MessageReactions';
import { useSessionSocket } from '../../hooks/useSessionSocket';
import { socketService } from '../../services/socket.service';
import { useNotification } from '../../context/NotificationContext';
import { useAuth } from '../../hooks/useAuth';
import PresenceBar from '../../components/instructor/PresenceBar';
import WhisperChat from '../../components/instructor/WhisperChat';

type FilterType = 'all' | 'high' | 'normal';

const avatarColors = ['primary.main', '#388e3c', '#f57c00', '#7b1fa2', '#c62828', '#00838f', '#4527a0', 'success.main'];

const getAvatarColor = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return avatarColors[Math.abs(hash) % avatarColors.length];
};

const getInitials = (firstName?: string, lastName?: string) => {
  return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || '?';
};

const getStudentName = (req: HelpRequest) => {
  return req.student
    ? `${req.student.firstName || ''} ${req.student.lastName || ''}`.trim()
    : 'Unknown Student';
};

const HelpQueue: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const showNotificationRef = useRef(showNotification);
  showNotificationRef.current = showNotification;

  // Left panel state
  const [requests, setRequests] = useState<HelpRequest[]>([]);
  const [stats, setStats] = useState<HelpRequestStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>('all');
  const [resolvedExpanded, setResolvedExpanded] = useState(false);

  // Right panel state
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [messages, setMessages] = useState<HelpRequestMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  // Instructor notes state
  const [instructorNotesExpanded, setInstructorNotesExpanded] = useState(false);

  // Claims state
  const [claims, setClaims] = useState<Map<string, { id: string; name: string }>>(new Map());

  // Resolve dialog state
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolveResponseText, setResolveResponseText] = useState('');
  const [resolving, setResolving] = useState(false);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const selectedRequestIdRef = useRef<string | null>(null);
  selectedRequestIdRef.current = selectedRequestId;

  const selectedRequest = requests.find((r) => r.id === selectedRequestId) || null;

  // --- Data loading ---

  const loadData = useCallback(async () => {
    if (!id) return;
    try {
      const [reqs, st] = await Promise.all([
        helpRequestService.getBySession(id),
        helpRequestService.getStats(id),
      ]);
      setRequests(reqs);
      setStats(st);
    } catch {
      showNotificationRef.current('Failed to load help requests', 'error');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadMessages = useCallback(async (helpRequestId: string) => {
    setMessagesLoading(true);
    try {
      const msgs = await helpRequestService.getMessages(helpRequestId);
      setMessages(msgs);
    } catch {
      showNotificationRef.current('Failed to load messages', 'error');
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Claims socket listeners
  useEffect(() => {
    if (!id) return;
    socketService.getActiveClaims(id);

    const unsubClaimed = socketService.onHelpRequestClaimed((data) => {
      setClaims(prev => new Map(prev).set(data.requestId, data.claimedBy));
    });

    const unsubUnclaimed = socketService.onHelpRequestUnclaimed((data) => {
      setClaims(prev => {
        const next = new Map(prev);
        next.delete(data.requestId);
        return next;
      });
    });

    const unsubActive = socketService.onActiveClaims((data) => {
      const map = new Map<string, { id: string; name: string }>();
      data.claims.forEach(c => map.set(c.requestId, c.claimedBy));
      setClaims(map);
    });

    return () => { unsubClaimed(); unsubUnclaimed(); unsubActive(); };
  }, [id]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleReactionsChange = useCallback((messageId: string, reactions: MessageReactionType[]) => {
    setMessages(prev => prev.map(m =>
      m.id === messageId ? { ...m, reactions } : m,
    ));
  }, []);

  // --- WebSocket real-time updates ---

  useSessionSocket({
    sessionId: id || '',
    onHelpRequest: (event) => {
      setRequests((prev) => [
        {
          id: event.id,
          sessionId: id || '',
          studentId: event.studentId,
          student: event.studentName
            ? {
                firstName: event.studentName.split(' ')[0],
                lastName: event.studentName.split(' ').slice(1).join(' '),
              } as HelpRequest['student']
            : undefined,
          stepId: event.stepId,
          message: event.message,
          status: 'open',
          priority: 'normal',
          requestedAt: new Date().toISOString(),
          messageCount: 0,
        } as HelpRequest,
        ...prev,
      ]);
      setStats((prev) => (prev ? { ...prev, activeCount: prev.activeCount + 1 } : prev));
      showNotificationRef.current(`New help request from ${event.studentName || 'a student'}`, 'info');
    },
    onHelpResolved: (event) => {
      setRequests((prev) =>
        prev.map((r) =>
          r.id === event.id
            ? { ...r, status: 'resolved' as const, resolvedAt: new Date().toISOString(), response: event.response }
            : r,
        ),
      );
      setStats((prev) => (prev ? { ...prev, activeCount: Math.max(0, prev.activeCount - 1) } : prev));
      setClaims(prev => {
        const next = new Map(prev);
        next.delete(event.id);
        return next;
      });
    },
    onHelpMessage: (event) => {
      // Update message count on the request card
      setRequests((prev) =>
        prev.map((r) =>
          r.id === event.helpRequestId
            ? { ...r, messageCount: (r.messageCount || 0) + 1, status: event.status as HelpRequest['status'] }
            : r,
        ),
      );

      // If this message is for the currently selected request, append it to the thread
      if (selectedRequestIdRef.current === event.helpRequestId) {
        const wsMsg: HelpRequestMessage = {
          id: event.messageId,
          helpRequestId: event.helpRequestId,
          userId: event.userId,
          userRole: event.userRole,
          message: event.message,
          createdAt: event.createdAt,
          user: event.userName
            ? {
                id: event.userId,
                firstName: event.userName.split(' ')[0],
                lastName: event.userName.split(' ').slice(1).join(' '),
              }
            : undefined,
        };
        setMessages((prev) => {
          // Avoid duplicates (in case the sender is the current user and the optimistic add already occurred)
          if (prev.some((m) => m.id === wsMsg.id)) return prev;
          return [...prev, wsMsg];
        });
      } else {
        // Notification for messages on other requests
        showNotificationRef.current(
          `New message from ${event.userName || 'someone'} on another help request`,
          'info',
        );
      }
    },
    onMessageReaction: (data) => {
      if (selectedRequestIdRef.current === data.helpRequestId) {
        helpRequestService.getMessages(data.helpRequestId).then(setMessages).catch(() => {});
      }
    },
  });

  // --- Handlers ---

  const handleClaim = (requestId: string) => {
    if (id) socketService.claimHelpRequest(id, requestId);
  };

  const handleUnclaim = (requestId: string) => {
    if (id) socketService.unclaimHelpRequest(id, requestId);
  };

  const handleSelectRequest = (reqId: string) => {
    setSelectedRequestId(reqId);
    setNewMessage('');
    loadMessages(reqId);
  };

  const handleSendMessage = async () => {
    if (!selectedRequestId || !newMessage.trim() || sending) return;
    setSending(true);
    try {
      const sent = await helpRequestService.addMessage(selectedRequestId, newMessage.trim());
      setMessages((prev) => {
        if (prev.some((m) => m.id === sent.id)) return prev;
        return [...prev, sent];
      });
      setNewMessage('');
      // Update message count on card
      setRequests((prev) =>
        prev.map((r) =>
          r.id === selectedRequestId ? { ...r, messageCount: (r.messageCount || 0) + 1 } : r,
        ),
      );
      // Auto-claim if not already claimed by me
      const currentClaim = claims.get(selectedRequestId);
      if (id && (!currentClaim || currentClaim.id !== user?.id)) {
        socketService.claimHelpRequest(id, selectedRequestId);
      }
    } catch {
      showNotification('Failed to send message', 'error');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleOpenResolve = () => {
    setResolveResponseText('');
    setResolveDialogOpen(true);
  };

  const handleResolve = async () => {
    if (!selectedRequest) return;
    setResolving(true);
    try {
      await helpRequestService.resolve(selectedRequest.id, resolveResponseText.trim() || undefined);
      setResolveDialogOpen(false);
      showNotification('Help request resolved', 'success');
      loadData();
      // Reload messages to reflect any final state
      loadMessages(selectedRequest.id);
    } catch {
      showNotification('Failed to resolve help request', 'error');
    } finally {
      setResolving(false);
    }
  };

  // --- Derived data ---

  const activeRequests = requests
    .filter((r) => r.status !== 'resolved')
    .sort((a, b) => {
      if (a.priority === 'high' && b.priority !== 'high') return -1;
      if (a.priority !== 'high' && b.priority === 'high') return 1;
      return new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime();
    });

  const resolvedRequests = requests
    .filter((r) => r.status === 'resolved')
    .sort((a, b) => new Date(b.resolvedAt || 0).getTime() - new Date(a.resolvedAt || 0).getTime());

  const filteredActive =
    filter === 'all' ? activeRequests : activeRequests.filter((r) => r.priority === filter);

  // --- Loading state ---

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // --- Request card renderer ---

  const renderRequestCard = (req: HelpRequest, isResolved = false) => {
    const studentName = getStudentName(req);
    const isHigh = req.priority === 'high';
    const isSelected = selectedRequestId === req.id;

    return (
      <Paper
        key={req.id}
        elevation={0}
        onClick={() => handleSelectRequest(req.id)}
        sx={{
          p: 2,
          mb: 1.5,
          cursor: 'pointer',
          borderLeft: 4,
          borderColor: isResolved ? 'success.main' : isHigh ? 'error.main' : 'warning.main',
          borderRadius: '8px',
          bgcolor: isSelected ? 'action.selected' : isResolved ? 'grey.50' : 'background.paper',
          opacity: isResolved ? 0.7 : 1,
          transition: 'background-color 0.2s, box-shadow 0.2s',
          '&:hover': {
            bgcolor: isSelected ? 'action.selected' : 'action.hover',
            boxShadow: 2,
          },
        }}
      >
        {/* Student info row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Avatar
            sx={{
              bgcolor: getAvatarColor(studentName),
              width: 36,
              height: 36,
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {getInitials(req.student?.firstName, req.student?.lastName)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>
              {studentName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {req.student?.email || ''}
            </Typography>
          </Box>
        </Box>

        {/* Priority + time row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Chip
            label={isHigh ? 'HIGH' : 'NORMAL'}
            size="small"
            color={isHigh ? 'error' : 'default'}
            sx={{ fontWeight: 600, fontSize: 10, height: 20 }}
          />
          <Typography variant="caption" color="text.secondary">
            {dayjs(req.requestedAt).fromNow()}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Chip
            label={req.status === 'open' ? 'Open' : req.status === 'in_progress' ? 'In Progress' : 'Resolved'}
            size="small"
            color={req.status === 'open' ? 'error' : req.status === 'in_progress' ? 'warning' : 'success'}
            variant="outlined"
            sx={{ fontSize: 10, height: 20 }}
          />
        </Box>

        {/* Step context */}
        {req.step && (
          <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 500, display: 'block', mb: 0.5 }}>
            Step {(req.step.orderIndex ?? 0) + 1}: {req.step.title}
          </Typography>
        )}

        {/* Message preview */}
        {req.message && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: 12,
              fontStyle: 'italic',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              mb: 0.5,
            }}
          >
            &ldquo;{req.message}&rdquo;
          </Typography>
        )}

        {/* Message count badge */}
        {(req.messageCount ?? 0) > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <ChatIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {req.messageCount} message{(req.messageCount ?? 0) > 1 ? 's' : ''}
            </Typography>
          </Box>
        )}

        {/* Claim UI */}
        {!isResolved && (() => {
          const claim = claims.get(req.id);
          const isClaimedByMe = claim?.id === user?.id;
          return (
            <Box sx={{ mt: 1 }}>
              {!claim && (
                <Button
                  size="small"
                  variant="text"
                  color="primary"
                  onClick={(e) => { e.stopPropagation(); handleClaim(req.id); }}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', p: 0, minWidth: 0 }}
                >
                  Claim
                </Button>
              )}
              {claim && (
                <Chip
                  size="small"
                  label={isClaimedByMe ? 'Claimed by you' : `Handled by ${claim.name}`}
                  color={isClaimedByMe ? 'primary' : 'default'}
                  variant="outlined"
                  onDelete={isClaimedByMe ? (e) => { e.stopPropagation(); handleUnclaim(req.id); } : undefined}
                  sx={{ fontSize: '0.7rem' }}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
            </Box>
          );
        })()}
      </Paper>
    );
  };

  // --- Right panel: conversation thread ---

  const renderConversationThread = () => {
    if (!selectedRequest) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: 'text.secondary',
          }}
        >
          <ChatIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
          <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
            Select a help request
          </Typography>
          <Typography variant="body2">
            Select a help request to view the conversation
          </Typography>
        </Box>
      );
    }

    const studentName = getStudentName(selectedRequest);
    const isResolved = selectedRequest.status === 'resolved';

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Thread header */}
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                bgcolor: getAvatarColor(studentName),
                width: 40,
                height: 40,
                fontSize: 15,
                fontWeight: 600,
              }}
            >
              {getInitials(selectedRequest.student?.firstName, selectedRequest.student?.lastName)}
            </Avatar>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {studentName}
                </Typography>
                <Chip
                  label={selectedRequest.priority === 'high' ? 'HIGH' : 'NORMAL'}
                  size="small"
                  color={selectedRequest.priority === 'high' ? 'error' : 'default'}
                  sx={{ fontWeight: 600, fontSize: 10, height: 20 }}
                />
                <Chip
                  label={
                    selectedRequest.status === 'open'
                      ? 'Open'
                      : selectedRequest.status === 'in_progress'
                        ? 'In Progress'
                        : 'Resolved'
                  }
                  size="small"
                  color={
                    selectedRequest.status === 'open'
                      ? 'error'
                      : selectedRequest.status === 'in_progress'
                        ? 'warning'
                        : 'success'
                  }
                  sx={{ fontSize: 10, height: 20 }}
                />
                {(() => {
                  const claim = claims.get(selectedRequest.id);
                  if (!claim) return null;
                  const isClaimedByMe = claim.id === user?.id;
                  return (
                    <Chip
                      size="small"
                      label={isClaimedByMe ? 'You claimed this' : `Claimed by ${claim.name}`}
                      color={isClaimedByMe ? 'primary' : 'warning'}
                      variant="filled"
                      sx={{ fontSize: 10, height: 20 }}
                    />
                  );
                })()}
              </Box>
              {selectedRequest.step && (
                <Typography variant="caption" color="text.secondary">
                  Step {(selectedRequest.step.orderIndex ?? 0) + 1}: {selectedRequest.step.title}
                </Typography>
              )}
            </Box>
          </Box>
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={<ResolvedIcon />}
            disabled={isResolved}
            onClick={handleOpenResolve}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {isResolved ? 'Resolved' : 'Resolve'}
          </Button>
        </Box>

        {/* Instructor Notes (collapsible) */}
        {selectedRequest.step && (selectedRequest.step as any).instructorNotes && (
          <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box
              onClick={() => setInstructorNotesExpanded(!instructorNotesExpanded)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <EyeSlashIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                Instructor Notes
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem' }}>
                Not visible to students
              </Typography>
              <Box sx={{ flex: 1 }} />
              {instructorNotesExpanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
            </Box>
            <Collapse in={instructorNotesExpanded}>
              <Box sx={{ px: 2, pb: 1.5 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.8125rem', color: 'text.secondary' }}>
                  {(selectedRequest.step as any).instructorNotes}
                </Typography>
              </Box>
            </Collapse>
          </Box>
        )}

        {/* Messages area */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          {messagesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress size={32} />
            </Box>
          ) : messages.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <Typography variant="body2" color="text.secondary">
                No messages yet. Start the conversation below.
              </Typography>
            </Box>
          ) : (
            <>
              {/* Show original request message as the first bubble if it exists */}
              {selectedRequest.message && messages.length > 0 && !messages.some((m) => m.message === selectedRequest.message) && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <Box
                    sx={{
                      maxWidth: '70%',
                      bgcolor: '#e3f2fd',
                      borderRadius: '12px 12px 12px 4px',
                      p: 1.5,
                      px: 2,
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {selectedRequest.message}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }}>
                        {studentName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11, ml: 2 }}>
                        {dayjs(selectedRequest.requestedAt).fromNow()}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {messages.map((msg) => {
                const isInstructor = msg.userRole === 'instructor' || msg.userRole === 'admin';
                const senderName = msg.user
                  ? `${msg.user.firstName || ''} ${msg.user.lastName || ''}`.trim()
                  : 'Unknown';

                return (
                  <Box
                    key={msg.id}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: isInstructor ? 'flex-end' : 'flex-start',
                      '&:hover .reaction-add-btn': { opacity: 1 },
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: '70%',
                        bgcolor: isInstructor ? '#f5f5f5' : '#e3f2fd',
                        borderRadius: isInstructor ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                        p: 1.5,
                        px: 2,
                      }}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {msg.message}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mt: 0.5,
                          gap: 2,
                        }}
                      >
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }}>
                          {senderName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }}>
                          {dayjs(msg.createdAt).fromNow()}
                        </Typography>
                      </Box>
                    </Box>
                    {selectedRequestId && (
                      <MessageReactions
                        helpRequestId={selectedRequestId}
                        messageId={msg.id}
                        reactions={msg.reactions || []}
                        currentUserId={user?.id || ''}
                        onReactionsChange={handleReactionsChange}
                      />
                    )}
                  </Box>
                );
              })}
            </>
          )}
          <div ref={messagesEndRef} />
        </Box>

        {/* Input area */}
        <Box
          sx={{
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'flex-end',
            gap: 1,
            flexShrink: 0,
          }}
        >
          <TextField
            fullWidth
            multiline
            maxRows={2}
            placeholder={isResolved ? 'This request has been resolved' : 'Type a message...'}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isResolved || sending}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
            }}
          />
          <IconButton
            color="primary"
            onClick={handleSendMessage}
            disabled={isResolved || sending || !newMessage.trim()}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' },
              '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
              width: 40,
              height: 40,
              borderRadius: '8px',
            }}
          >
            {sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
          </IconButton>
        </Box>
      </Box>
    );
  };

  // --- Main render ---

  return (
    <Box>
      {/* Page header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          size="small"
          startIcon={<BackIcon />}
          onClick={() => navigate(`/instructor/live/${id}`)}
          sx={{ color: 'text.secondary' }}
        >
          Back to Live Dashboard
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Help Request Queue
        </Typography>
      </Box>

      {/* Presence Bar */}
      <PresenceBar sessionId={id!} currentPage="help-queue" />

      {/* Split view */}
      <Box sx={{ display: 'flex', gap: 2.5, height: 'calc(100vh - 160px)' }}>
        {/* Left Panel — Request List (40%) */}
        <Box sx={{ width: '40%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {/* Stats row */}
          <Grid container spacing={1.5} sx={{ mb: 2, flexShrink: 0 }}>
            <Grid size={{ xs: 4 }}>
              <Paper elevation={1} sx={{ p: 1.5, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                  Active Requests
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, color: (stats?.activeCount || 0) > 0 ? '#d32f2f' : 'text.primary' }}
                >
                  {stats?.activeCount ?? 0}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Paper elevation={1} sx={{ p: 1.5, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                  Avg Response
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {stats?.avgResponseTime ?? 0}
                  <Typography component="span" variant="caption" sx={{ fontWeight: 500, ml: 0.5 }}>
                    min
                  </Typography>
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Paper elevation={1} sx={{ p: 1.5, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                  Resolved Today
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#4caf50' }}>
                  {stats?.resolvedToday ?? 0}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Filter chips */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexShrink: 0 }}>
            {([
              { key: 'all', label: 'All' },
              { key: 'high', label: 'High Priority' },
              { key: 'normal', label: 'Normal Priority' },
            ] as { key: FilterType; label: string }[]).map((f) => (
              <Chip
                key={f.key}
                label={f.label}
                size="small"
                variant={filter === f.key ? 'filled' : 'outlined'}
                color={filter === f.key ? 'primary' : 'default'}
                onClick={() => setFilter(f.key)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>

          {/* Scrollable request list */}
          <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>
            {filteredActive.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <ResolvedIcon sx={{ fontSize: 48, color: '#4caf50', mb: 1 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                  No open help requests
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  All students are doing great!
                </Typography>
              </Paper>
            ) : (
              filteredActive.map((req) => renderRequestCard(req))
            )}

            {/* Resolved requests accordion */}
            {resolvedRequests.length > 0 && (
              <Accordion
                expanded={resolvedExpanded}
                onChange={(_, expanded) => setResolvedExpanded(expanded)}
                elevation={0}
                disableGutters
                sx={{
                  mt: 1.5,
                  '&:before': { display: 'none' },
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '8px !important',
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    Resolved ({resolvedRequests.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 1, pt: 0 }}>
                  {resolvedRequests.map((req) => renderRequestCard(req, true))}
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        </Box>

        {/* Right Panel — Conversation Thread (60%) */}
        <Paper
          elevation={0}
          sx={{
            width: '60%',
            borderRadius: '12px',
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {renderConversationThread()}
        </Paper>
      </Box>

      {/* Resolve Dialog */}
      <Dialog
        open={resolveDialogOpen}
        onClose={() => setResolveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Resolve this help request?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Optionally add a closing message before resolving.
          </Typography>
          <TextField
            multiline
            rows={3}
            fullWidth
            label="Closing message (optional)"
            value={resolveResponseText}
            onChange={(e) => setResolveResponseText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleResolve}
            disabled={resolving}
          >
            {resolving ? <CircularProgress size={20} color="inherit" /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Whisper Chat */}
      <WhisperChat sessionId={id!} />
    </Box>
  );
};

export default HelpQueue;
